import { Message, useMessage } from '@messageformat/react';
import classes from './ResultTile.module.scss';
import { bulletColors, getPercent } from './utils';
import Score from '../Score/Score';
import Donut from '../Donut/Donut';
import ColorBulletListItem from '../../ColorBulletListItem';

interface Props {
  title: string;
  index: number;
  shares: {
    label: string;
    value: number;
  }[];
  scoreMax: number;
}

const ResultTile = ({ title, index, shares, scoreMax }: Props): JSX.Element => {
  const otherLabel = useMessage('course7.task3.tab1.locations.other');

  // Get total amount of shares to calculate percentage
  const sharesTotal = shares.reduce((accumulated, current) => {
    return accumulated + current.value;
  }, 0);

  // Get percent of available items selected
  const scorePercent = getPercent(scoreMax, shares.length);

  // Check if something is selected
  const isData: boolean = shares.length > 0;

  // Squash shares with less than 2% value into "other" if there are more than 11 shares
  let processedShares = shares;
  if (shares.length > 11) {
    const threshold = sharesTotal * 0.02;
    const significantShares = shares.filter((share) => share.value >= threshold);
    const otherShares = shares.filter((share) => share.value < threshold);

    const otherValue = otherShares.reduce(
      (accumulated, current) => accumulated + current.value,
      0,
    );

    const existingOtherShare = significantShares.find(
      (share) => share.label === otherLabel,
    );
    if (existingOtherShare) {
      existingOtherShare.value += otherValue;
    } else if (otherValue > 0) {
      significantShares.push({ label: otherLabel, value: otherValue });
    }

    processedShares = significantShares;
  }

  // Sort shares by value in descending order
  processedShares.sort((a, b) => b.value - a.value);

  return (
    <div className={classes.container} key={`diversityTile ${index}`}>
      <div className={classes.headerAndGraphContainer}>
        <div className={classes.headerContainer}>
          <h4 className={classes.header}>{title}</h4>
        </div>
        <div className={classes.graphContainer}>
          <Donut data={processedShares} />
        </div>
        <div className={classes.listContainer}>
          {isData ? (
            <ul className={classes.list}>
              {processedShares.map((element, idx) => (
                <ColorBulletListItem
                  color={bulletColors[idx]}
                  key={element.label}
                  className="opds-styles-v1-text-body-small"
                >
                  {`${element.label} ${getPercent(sharesTotal, element.value).toFixed(1)} %`}
                </ColorBulletListItem>
              ))}
            </ul>
          ) : (
            <Message id="course7.emptyportfolio" />
          )}
        </div>
      </div>
      <div className={classes.scoreContainer}>
        <Score scorePercent={scorePercent} showText={isData} />
      </div>
    </div>
  );
};

export default ResultTile;
